import React from "react";
import classnames from "classnames";
import map from "lodash/map";

import {
    iconUses,
    formats,
    licenses,
    licensesDisplay,
    constToCSS
} from "../../reducers/download";

import DownloadFlowErrors from "./download-flow-errors";
import LicenseDisplay from "./license-display";
import LicenseStrip from "./license-strip";
import IconSaveForLater from "./icon-save-for-later";
import IconCollection from "./icon-collection";
import IconTags from "./icon-tags";
import ToolTip from "../tool-tip";
import { ExportSizesControl } from "../editor/editor-controls";

const CreativeCommonsDownloadReady = props => {
    let { attributionType, pendingDownload } = props.download;
    let { downloadResetAttribution } = props;
    let attributionInstructions;
    if (attributionType) {
        let useType = iconUses[attributionType];
        let instructions = map(useType.instructions, (line, i) => {
            if (line.url) {
                return (
                    <li className="no-list-decoration" key={i}>
                        <a href={line.url} target="_blank">
                            {line.text}
                        </a>
                    </li>
                );
            }
            return <li key={i}>{line}</li>;
        });

        let attributionText;

        if (pendingDownload.uploader) {
            attributionText =
                pendingDownload.term +
                " by " +
                pendingDownload.uploader.name +
                " from the Noun Project";
        }

        return (
            <div className="license-specific-content">
                <h3
                    className="selected-attribution"
                    onClick={downloadResetAttribution}
                >
                    <div>
                        <div className="title">{useType.title}</div>
                        <div className="link-action">Choose another reason</div>
                    </div>
                </h3>
                <div className="attribution-instructions">
                    <h3>Here's how you should attribute:</h3>
                    <ol>{instructions}</ol>
                    <h3>Attribution text</h3>
                    <div className="copy-attribution">
                        <textarea
                            id="copy-attribution"
                            className="copy-input"
                            value={attributionText}
                            readOnly="true"
                            onClick={selectText}
                        />
                    </div>
                </div>
            </div>
        );
    }
};

const selectText = event => event.target.select();

const PhaseDownloadReady = props => {
    let {
        pendingDownload,
        license,
        processing,
        format,
        justPurchased
    } = props.download;

    // Show supplementary info on direct to Download Ready flows
    let showInfo =
        license != licenses.ROYALTY_FREE &&
        license != licenses.CREATIVE_COMMONS;

    let tags = <IconTags {...props} />;

    let licenseSpecificContent;
    if (license == licenses.CREATIVE_COMMONS) {
        licenseSpecificContent = CreativeCommonsDownloadReady(props);
        tags = null;
    } else if (justPurchased) {
        let licenseDisplay = licensesDisplay[license];

        if (licenseDisplay && licenseDisplay.thankYouText) {
            licenseSpecificContent = (
                <div className="license-thank-you license-specific-content">
                    <h1>{licenseDisplay.thankYouTitle}</h1>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: licenseDisplay.thankYouText
                        }}
                    />
                </div>
            );
        }
    }

    let repurchase;
    if (license == licenses.ALREADY_PURCHASED_ROYALTY_FREE) {
        let repurchaseHelp = (
            <ToolTip>
                <p>Repurchase if you need an extended use license.</p>
                <a
                    className="text-link"
                    href="https://thenounproject.zendesk.com/hc/en-us/articles/200562967-Can-I-use-icons-in-Items-for-Resale-"
                    target="_blank"
                >
                    Learn more
                </a>
            </ToolTip>
        );

        repurchase = (
            <div className="repurchase-license">
                <span
                    className="link-action"
                    onClick={props.downloadRepurchase.bind(
                        null,
                        pendingDownload
                    )}
                >
                    Purchase another license
                </span>{" "}
                {repurchaseHelp}
            </div>
        );
    }

    let canExportSize = license != licenses.CREATIVE_COMMONS;
    let exportSize =
        canExportSize &&
        props.editor &&
        props.editor.transforms &&
        props.editor.transforms.exportSize;

    let formatList = formats.map(formatOption => {
        let options;
        let formatDisplay = formatOption;

        let onButtonClick = props.downloadStart.bind(null, formatOption);

        if (canExportSize) {
            if (exportSize && formatOption == "png") {
                let control = (
                    <span>
                        <i className="ui_down-drop-arrow" />
                    </span>
                );
                options = (
                    <ExportSizesControl
                        control={control}
                        exportSize={exportSize}
                        editorTransform={props.editorTransform}
                    />
                );

                formatDisplay = (
                    <span className="button-target" onClick={onButtonClick}>
                        {formatDisplay}
                    </span>
                );

                onButtonClick = null;
            }
        }

        let formatClass = classnames("option format-option button", {
            current: format == formatOption,
            "with-options": !!options
        });

        return (
            <li
                className={formatClass}
                key={formatOption}
                onClick={onButtonClick}
            >
                {formatDisplay}
                {options}
            </li>
        );
    });

    let formatHelp = (
        <ToolTip className="format-tool-tip">
            <p>
                Icons can be downloaded as two different file types, PNG or SVG.
            </p>
            <p>
                <b>PNG</b> are raster images that can be used anywhere you can
                use a photo. They are best for use in mediums like presentations
                or wireframes. Pixelation may occur when scaling up.
            </p>

            <p>
                <b>SVG</b> are vector images that are infinitely scalable, so
                they never pixelate. They are best for use in mediums like print
                and websites.
            </p>
            <a
                href="https://thenounproject.zendesk.com/hc/en-us/articles/204515707-What-s-the-difference-between-a-PNG-and-SVG-"
                target="_blank"
                className="text-link"
            >
                Read More
            </a>
        </ToolTip>
    );

    let saveForLater, collection;
    if (showInfo) {
        collection = <IconCollection {...props} />;
        saveForLater = <IconSaveForLater {...props} />;
    }

    let classes = classnames("download-ready-container", {
        processing
    });

    let licenseStrip = license != licenses.CREATIVE_COMMONS && (
        <LicenseStrip license={license} />
    );

    let exportInfo;
    if (canExportSize) {
        exportInfo = (
            <div className="export-info">
                {exportSize}px x {exportSize}px
            </div>
        );
    }

    return (
        <div className={classes}>
            {collection}
            {licenseSpecificContent}
            {licenseStrip}
            {repurchase}
            <div className="download-actions">
                <h4>Download Icon {formatHelp}</h4>
                <div className="relative-wrapper">
                    <ul className="format-options">{formatList}</ul>
                    {exportInfo}
                    <DownloadFlowErrors error={props.download.error} />
                    {saveForLater}
                </div>
            </div>
            {tags}
        </div>
    );
};

PhaseDownloadReady.handlesOwnErrors = true;

export default PhaseDownloadReady;
